import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

const Extend = ({
  open,
  onClose,
  currentExpireAt,
  onExtend,
  loading = false,
}) => {
  const [newExpiryDate, setNewExpiryDate] = useState("");
  const [dateError, setDateError] = useState("");

  const normalizeTimestamp = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (value instanceof Date) {
      const timestamp = value.getTime();

      return Number.isNaN(timestamp) ? null : timestamp;
    }

    if (typeof value === "number") {
      if (!Number.isFinite(value)) {
        return null;
      }

      return value < 100000000000 ? value * 1000 : value;
    }

    const stringValue = String(value).trim();

    if (!stringValue) {
      return null;
    }

    if (/^\d+(\.\d+)?$/.test(stringValue)) {
      const numericValue = Number(stringValue);

      if (!Number.isFinite(numericValue)) {
        return null;
      }

      return numericValue < 100000000000 ? numericValue * 1000 : numericValue;
    }

    const parsedTimestamp = Date.parse(stringValue);

    return Number.isNaN(parsedTimestamp) ? null : parsedTimestamp;
  };

  const formatDateForInput = (value) => {
    const timestamp = normalizeTimestamp(value);

    if (timestamp === null) {
      return "";
    }

    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Taipei",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(timestamp));

    const year = parts.find((part) => part.type === "year")?.value;

    const month = parts.find((part) => part.type === "month")?.value;

    const day = parts.find((part) => part.type === "day")?.value;

    if (!year || !month || !day) {
      return "";
    }

    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (value) => {
    const timestamp = normalizeTimestamp(value);

    if (timestamp === null) {
      return "-";
    }

    const date = new Date(timestamp);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Taipei",
    }).format(date);
  };

  const convertDateInputToTimestamp = (dateString) => {
    if (!dateString) {
      return null;
    }

    const matched = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!matched) {
      return null;
    }

    const [, year, month, day] = matched;

    const timestamp = Date.parse(`${year}-${month}-${day}T15:59:59.999Z`);

    return Number.isNaN(timestamp) ? null : timestamp;
  };

  const convertDateInputToLocalDate = (dateString) => {
    if (!dateString) {
      return null;
    }

    const matched = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!matched) {
      return null;
    }

    const [, year, month, day] = matched;

    const date = new Date(Number(year), Number(month) - 1, Number(day));

    date.setHours(0, 0, 0, 0);

    return Number.isNaN(date.getTime()) ? null : date;
  };

  const currentExpireAtMilliseconds = useMemo(() => {
    return normalizeTimestamp(currentExpireAt);
  }, [currentExpireAt]);

  const today = useMemo(() => {
    const date = new Date();

    date.setHours(0, 0, 0, 0);

    return date;
  }, [open]);

  const maxExpiryDate = useMemo(() => {
    const date = new Date();

    date.setHours(0, 0, 0, 0);

    date.setDate(date.getDate() + 89);

    return date;
  }, [open]);

  const minExpiryDateString = useMemo(() => {
    return formatDateForInput(today);
  }, [today]);

  const maxExpiryDateString = useMemo(() => {
    return formatDateForInput(maxExpiryDate);
  }, [maxExpiryDate]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setDateError("");

    if (currentExpireAtMilliseconds === null) {
      setNewExpiryDate("");
      return;
    }

    const currentExpiryString = formatDateForInput(currentExpireAtMilliseconds);

    const currentExpiryDate = convertDateInputToLocalDate(currentExpiryString);

    if (!currentExpiryDate) {
      setNewExpiryDate("");
      return;
    }

    if (currentExpiryDate < today) {
      setNewExpiryDate(minExpiryDateString);
      return;
    }

    if (currentExpiryDate > maxExpiryDate) {
      setNewExpiryDate("");
      setDateError(
        "The current expiry is already more than 90 days from today.",
      );
      return;
    }

    setNewExpiryDate(currentExpiryString);
  }, [
    open,
    currentExpireAtMilliseconds,
    today,
    maxExpiryDate,
    minExpiryDateString,
  ]);

  const getValidationError = () => {
    if (!newExpiryDate) {
      return "";
    }

    const selectedDate = convertDateInputToLocalDate(newExpiryDate);

    if (!selectedDate) {
      return "Please select a valid expiry date.";
    }

    if (selectedDate < today) {
      return "New expiry cannot be earlier than today.";
    }

    if (selectedDate > maxExpiryDate) {
      return "New expiry cannot be more than 90 days from today.";
    }

    const newExpireAt = convertDateInputToTimestamp(newExpiryDate);

    if (newExpireAt === null) {
      return "Please select a valid expiry date.";
    }

    if (
      currentExpireAtMilliseconds !== null &&
      newExpireAt <= currentExpireAtMilliseconds
    ) {
      return "New expiry must be later than the current expiry.";
    }

    return "";
  };

  const validationError = dateError || getValidationError();

  const isInvalidDate = () => {
    if (!newExpiryDate) {
      return true;
    }

    return Boolean(validationError);
  };

  const handleDateChange = (event) => {
    const value = event.target.value;

    setDateError("");

    if (!value) {
      setNewExpiryDate("");
      return;
    }

    const selectedDate = convertDateInputToLocalDate(value);

    if (!selectedDate) {
      setNewExpiryDate("");
      setDateError("Please select a valid expiry date.");
      return;
    }

    if (selectedDate < today) {
      setDateError("New expiry cannot be earlier than today.");
      return;
    }

    if (selectedDate > maxExpiryDate) {
      setDateError("New expiry cannot be more than 90 days from today.");
      return;
    }

    setNewExpiryDate(value);
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    setDateError("");
    onClose?.();
  };

  const handleExtend = () => {
    if (loading || isInvalidDate()) {
      return;
    }

    const newExpireAt = convertDateInputToTimestamp(newExpiryDate);

    if (newExpireAt === null) {
      setDateError("Please select a valid expiry date.");
      return;
    }

    onExtend?.(newExpireAt);
  };

  return (
    <Modal
      open={Boolean(open)}
      onClose={handleClose}
      aria-labelledby="extend-support-token-title"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(31, 42, 68, 0.42)",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",

          width: "504px",
          maxWidth: "calc(100vw - 32px)",

          display: "flex",
          flexDirection: "column",

          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 18px 50px rgba(25, 35, 70, 0.25)",
          outline: "none",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            minHeight: "60px",
            px: "14px",
            py: "12px",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #E1E5EE",
          }}
        >
          <Typography
            id="extend-support-token-title"
            sx={{
              color: "#172B68",
              fontSize: "20px",
              fontWeight: 700,
            }}
          >
            Extend support token
          </Typography>

          <IconButton
            onClick={handleClose}
            disabled={loading}
            aria-label="Close"
            sx={{
              width: "36px",
              height: "36px",
              color: "#8A94A8",

              "&:hover": {
                backgroundColor: "#F3F5F9",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            px: "13px",
            py: "22px",
          }}
        >
          <Typography
            sx={{
              mb: "12px",
              color: "#70798C",
              fontSize: "14px",
              lineHeight: 1.45,
            }}
          >
            The same token string is kept — only the expiry moves. Select a date
            from today through 90 days from today.
          </Typography>

          <Box sx={{ mb: "14px" }}>
            <Typography
              sx={{
                mb: "5px",
                color: "#20377C",
                fontSize: "14px",
                fontWeight: 700,
              }}
            >
              Current expiry
            </Typography>

            <TextField
              fullWidth
              disabled
              value={formatDisplayDate(currentExpireAt)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  borderRadius: "7px",
                  backgroundColor: "#F5F6FA",

                  "& fieldset": {
                    borderColor: "#CBD2E1",
                  },

                  "&.Mui-disabled fieldset": {
                    borderColor: "#CBD2E1",
                  },
                },

                "& .MuiInputBase-input": {
                  fontSize: "14px",
                },

                "& .MuiInputBase-input.Mui-disabled": {
                  color: "#747D8F",
                  WebkitTextFillColor: "#747D8F",
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                mb: "5px",
                color: "#20377C",
                fontSize: "14px",
                fontWeight: 700,
              }}
            >
              New expiry *
            </Typography>

            <TextField
              fullWidth
              type="date"
              disabled={loading}
              value={newExpiryDate}
              onChange={handleDateChange}
              inputProps={{
                min: minExpiryDateString,
                max: maxExpiryDateString,
              }}
              error={Boolean(validationError)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  borderRadius: "7px",

                  "& fieldset": {
                    borderColor: "#CBD2E1",
                  },

                  "&:hover fieldset": {
                    borderColor: "#AAB4C7",
                  },

                  "&.Mui-focused fieldset": {
                    borderWidth: "1px",
                    borderColor: "#315DFF",
                  },

                  "&.Mui-error fieldset": {
                    borderColor: "#D32F2F",
                  },
                },

                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: "#4F5665",
                },

                "& .MuiFormHelperText-root": {
                  mx: 0,
                  mt: "6px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            minHeight: "72px",
            px: "13px",
            py: "14px",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: "1px solid #E1E5EE",
            backgroundColor: "#FAFBFD",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
            sx={{
              minWidth: "84px",
              height: "38px",
              px: "18px",
              borderRadius: "20px",
              borderColor: "#C7CFDF",
              color: "#20377C",
              backgroundColor: "#FFFFFF",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 700,

              "&:hover": {
                borderColor: "#9FAAC0",
                backgroundColor: "#F4F6FA",
              },

              "&.Mui-disabled": {
                color: "#98A2B3",
                borderColor: "#D0D5DD",
                backgroundColor: "#F2F4F7",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={loading || isInvalidDate()}
            onClick={handleExtend}
            startIcon={
              loading ? (
                <CircularProgress
                  size={16}
                  thickness={5}
                  sx={{
                    color: "#FFFFFF",
                  }}
                />
              ) : null
            }
            sx={{
              minWidth: loading ? "130px" : "86px",
              height: "38px",
              px: "18px",
              borderRadius: "20px",
              backgroundColor: "#20377C",
              color: "#FFFFFF",
              textTransform: "none",
              boxShadow: "none",
              fontSize: "14px",
              fontWeight: 700,

              "&:hover": {
                backgroundColor: "#172B68",
                boxShadow: "none",
              },

              "&.Mui-disabled": {
                color: "#FFFFFF",
                backgroundColor: loading ? "#20377C" : "#AEB6CA",
              },
            }}
          >
            {loading ? "Extending..." : "Extend"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default Extend;
